import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import amplitude from 'amplitude-js';
import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { setNewFields, updateFields, updateFormProperties } from 'apps/auth/actions/formAction';
import { setRequestDeliveryMethod } from 'apps/auth/actions/requestAction';
import { sendGtEvent } from 'apps/auth/analytics/analytics';
import { Button } from 'apps/auth/components/common/Button/Button';
import BaseLayout from 'apps/auth/components/common/Layout/BaseLayout';
import { CommonPage } from 'apps/auth/components/common/Layout/CommonPage/CommonPage';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import Form from 'apps/auth/components/form-multi/Form';
import ProfileFieldsEdit from 'apps/auth/components/profile/ProfileFieldsEdit';
import RequestLibreFlow from 'apps/auth/components/request/components/libre/RequestLibreFlow';
import CreateRequestPopups from 'apps/auth/components/request/CreateRequestPopups';
import FromFields from 'apps/auth/components/request/FromFields';
import NewAccountScreen from 'apps/auth/components/request/NewAccountScreen';
import NewCardScreen from 'apps/auth/components/request/NewCardScreen';
import PaymentMethodScreen from 'apps/auth/components/request/PaymentMethodScreen';
import RequestCalculatorScreen from 'apps/auth/components/request/RequestCalculatorScreen';
import {
    CALCULATOR_SCREEN,
    PHONE_SCREEN,
    REQUEST_ADDITIONAL_FIELDS,
    REQUEST_DELIVERY_METHODS,
    REQUEST_LIBRE_FLOW,
    REQUEST_NEW_ACCOUNT,
    REQUEST_NEW_CARD,
} from 'apps/auth/components/request/RequestConstants';
import ToFields from 'apps/auth/components/request/ToFields';
import { getFieldsForLibre } from 'apps/auth/components/request/utils';
import BasePage from 'apps/auth/components/utils/BasePage';
import {
    REQUESTEE_MOBILE_NUMBER_ADDED,
    REQUESTS_CALCULATOR_ADJUSTED,
    REQUESTS_CONFIRMATION_SHOWN,
    REQUESTS_NEW_METHOD_ADDED,
} from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_COUNTRY, PAYMENT_METHOD_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { requestPaymentMethods } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { REQUEST_CREATE_PRECHECK_POPUP } from 'apps/auth/constants/ComponentNames';
import { DC, DELIVERY_BANK_ACCOUNT, DELIVERY_BANK_CARD, DELIVERY_LIBRE_PAYSEND_LIVE } from 'apps/auth/constants/Constants';
import { SOURCE, SOURCE_CODE_FROM, SPECIAL_FIELD_PREFIX } from 'apps/auth/constants/fieldsName';
import { REQUEST_FORM_NAME } from 'apps/auth/constants/formName';
import { GT_OUT_REQUEST_SEND } from 'apps/auth/constants/GoogleAnalyticsEvents';
import { AUTH_REQUEST_CREATE, AUTH_REQUEST_MAIN_LINK, AUTH_REQUEST_PRECHECK } from 'apps/auth/constants/UrlConstants';
import { useExtra, useFields, useForm, usePageMessages } from 'apps/auth/hooks/selectors';
import { GTRequestMultiScreenService } from 'apps/auth/services/GTRequestMultiScreenService';

const RequestPage = (props) => {
    const { requestContinueButtonTitle, statusTitle, statusButtonDone, cardAdded, bankAccountAdded } = usePageMessages();
    const { profileNotFull, profileLegacyFields, sourcesToBankCard, sourcesToBankAccount, fromCountry, toCountry, showLibreStories, sourceToLibre } =
        useExtra();
    const fields = useFields(REQUEST_FORM_NAME);

    const [showStatus, toggleShowStatus] = useState(false);
    const [disabledSubmitButton, setDisabledSubmitButton] = useState(true);
    const [screen, setScreen] = useState(PHONE_SCREEN);
    const [isNewAccount, setIsNewAccount] = useState(false);
    const submitRef = useRef(null);
    const isNewCardRef = useRef(true);
    const { current: requestScreenService } = useRef(new GTRequestMultiScreenService());
    const popupRef = useRef(null);
    const { action } = useForm(REQUEST_FORM_NAME);
    const [popup, setPopup] = useState(REQUEST_CREATE_PRECHECK_POPUP);

    useEffect(() => {
        if (__isBrowser__) {
            sendGtEvent(GT_OUT_REQUEST_SEND);
        }
    }, []);

    useEffect(() => {
        if (fields && screen !== PHONE_SCREEN) {
            GTRequestMultiScreenService.toggleContinueButton(fields, screen, setDisabledSubmitButton);
        }
    }, [fields, screen]);

    useEffect(() => {
        if (showStatus) {
            amplitude.getInstance().logEvent(REQUESTS_CONFIRMATION_SHOWN, { [PAYMENT_COUNTRY]: fromCountry?.nameEn });
        }
    }, [showStatus]);

    const showPreCheck = () => {
        popupRef.current?.open();
    };

    const hasDcSources = (sources = {}) => {
        let hasDC = false;
        if (sources) {
            Object.keys(sources).map((key) => {
                if (key.indexOf(DC) > 0) {
                    hasDC = true;
                }
            });
            return hasDC;
        }
        return hasDC;
    };

    const setupScreenCounter = (fields = {}) => {
        let screenCounter = 0;
        const isNewSource = !fields?.[SOURCE]?.value;
        const hasToFields = Object.values(fields).some((field) => field?.visible && 'TO' === field?.destination && !field.name.includes(SPECIAL_FIELD_PREFIX));
        if (isNewSource) {
            screenCounter++;
        }
        if (hasToFields) {
            screenCounter++;
        }
        requestScreenService.setScreenCounter(screenCounter);
    };
    const submitHandler = (_fields, json) => {
        const newFields = json?.forms?.[REQUEST_FORM_NAME]?.fields;

        if (screen === REQUEST_DELIVERY_METHODS || screen === REQUEST_NEW_CARD || screen === REQUEST_NEW_ACCOUNT) {
            setupScreenCounter(newFields);
        }

        if (action === AUTH_REQUEST_CREATE && newFields) {
            props.setNewFields(REQUEST_FORM_NAME, newFields);
            if (!json?.extra?.precheck && !requestScreenService.isFinalScreen()) {
                switchToNextScreen(newFields);
            }
        }
        props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_PRECHECK, true);
        if (Object.keys(json.errors).length === 0) {
            if (json?.extra?.precheck) {
                if (screen === REQUEST_NEW_CARD) {
                    amplitude.getInstance().logEvent(REQUESTS_NEW_METHOD_ADDED, { [PAYMENT_METHOD_AMPLITUDE]: requestPaymentMethods.CARD });
                    props.toggleModalWithTimer(true, cardAdded, () => {
                        showPreCheck();
                        switchToNextScreen();
                        props.toggleModalWithTimer(false, '', false);
                    });
                } else if (screen === REQUEST_NEW_ACCOUNT) {
                    if (isNewAccount) {
                        amplitude.getInstance().logEvent(REQUESTS_NEW_METHOD_ADDED, { [PAYMENT_METHOD_AMPLITUDE]: requestPaymentMethods.ACCOUNT });
                    }
                    props.toggleModalWithTimer(true, bankAccountAdded, () => {
                        showPreCheck();
                        switchToNextScreen();
                        props.toggleModalWithTimer(false, '', false);
                    });
                } else if (requestScreenService.isFinalScreen()) {
                    showPreCheck();
                } else {
                    switchToNextScreen(newFields);
                }
            } else if (json.extra?.showStatus) {
                toggleShowStatus(true);
            }
        }
    };

    const switchToNextScreen = (fields = {}) => {
        const isDC_Source = !!fields?.[SOURCE]?.value;
        const hasDcSource = hasDcSources(sourcesToBankCard);
        const hasAdditionalFields = Object.values(fields).some(
            (field) => field?.visible && 'TO' === field?.destination && !field.name.includes(SPECIAL_FIELD_PREFIX),
        );
        if (screen === PHONE_SCREEN) {
            setScreen(CALCULATOR_SCREEN);
        } else if (screen === CALCULATOR_SCREEN && showLibreStories) {
            setScreen(REQUEST_LIBRE_FLOW);
        } else if (screen === CALCULATOR_SCREEN && !hasDcSource && !hasAdditionalFields) {
            requestScreenService.setScreenCounter(0);
            setScreen(isNewCardRef.current ? REQUEST_NEW_CARD : REQUEST_NEW_ACCOUNT);
        } else if (screen === CALCULATOR_SCREEN) {
            setScreen(REQUEST_DELIVERY_METHODS);
        } else if (screen === REQUEST_DELIVERY_METHODS) {
            requestScreenService.goAHeadScreen();
            if (isDC_Source && hasAdditionalFields) {
                setScreen(REQUEST_ADDITIONAL_FIELDS);
            } else {
                setScreen(isNewCardRef.current ? REQUEST_NEW_CARD : REQUEST_NEW_ACCOUNT);
            }
        } else if (screen === REQUEST_ADDITIONAL_FIELDS) {
            requestScreenService.goAHeadScreen();
            setScreen(REQUEST_DELIVERY_METHODS);
        } else if (screen === REQUEST_NEW_CARD || screen === REQUEST_NEW_ACCOUNT) {
            requestScreenService.setScreenCounter(0);
            if (hasAdditionalFields) {
                requestScreenService.goAHeadScreen();
                setScreen(REQUEST_ADDITIONAL_FIELDS);
            }
        }
    };

    const onBackIconClick = () => {
        const isNewSource = !fields?.[SOURCE]?.value;
        const hasDcSource = hasDcSources(sourcesToBankCard);
        const hasVisibleAdditionalFields = Object.values(fields).some(
            (field) => field?.visible && 'TO' === field?.destination && field.value !== '' && field.value != null,
        );
        if (screen === REQUEST_DELIVERY_METHODS) {
            setScreen(CALCULATOR_SCREEN);
        } else if (screen === CALCULATOR_SCREEN) {
            setScreen(PHONE_SCREEN);
        } else if ((screen === REQUEST_NEW_CARD || screen === REQUEST_NEW_ACCOUNT) && !hasDcSource && !hasVisibleAdditionalFields) {
            requestScreenService.goBackScreen();
            setScreen(CALCULATOR_SCREEN);
        } else if (screen === REQUEST_NEW_CARD || screen === REQUEST_NEW_ACCOUNT) {
            requestScreenService.goBackScreen();
            setScreen(REQUEST_DELIVERY_METHODS);
        } else if (screen === REQUEST_ADDITIONAL_FIELDS) {
            requestScreenService.goBackScreen();
            if (isNewSource) {
                setScreen(isNewCardRef.current ? REQUEST_NEW_CARD : REQUEST_NEW_ACCOUNT);
            } else {
                setScreen(REQUEST_DELIVERY_METHODS);
            }
        }
    };

    const screenValidation = (e) => {
        switch (screen) {
            case REQUEST_DELIVERY_METHODS:
            case REQUEST_ADDITIONAL_FIELDS: {
                return;
            }
            case REQUEST_NEW_CARD:
            case REQUEST_LIBRE_FLOW:
            case REQUEST_NEW_ACCOUNT: {
                if (!requestScreenService.isFinalScreen()) {
                    e.preventDefault();
                    switchToNextScreen(fields);
                }
                return;
            }
            default: {
                e.preventDefault();
                switchToNextScreen(fields);
            }
        }
    };

    const addNewCard = (item = {}) => {
        addNewSource(item, true);
    };
    const addNewAccount = (item = {}) => {
        addNewSource(item, false);
    };

    const chooseLibreDelivery = () => {
        Promise.resolve(props.updateFields(REQUEST_FORM_NAME, getFieldsForLibre(sourceToLibre, fields))).then(() => {
            props.setRequestDeliveryMethod(DELIVERY_LIBRE_PAYSEND_LIVE).then(() => submitRef.current.click());
        });
    };

    const addNewSource = (_item = null, isNewCard = true) => {
        isNewCardRef.current = isNewCard;
        props.setRequestDeliveryMethod(isNewCard ? DELIVERY_BANK_CARD : DELIVERY_BANK_ACCOUNT).then(() => {
            props.updateFormProperties(REQUEST_FORM_NAME, AUTH_REQUEST_CREATE, false).then(() => submitRef.current.click());
        });
    };

    const isHiddenButton = () => {
        return screen === REQUEST_DELIVERY_METHODS || screen === REQUEST_LIBRE_FLOW;
    };

    const getBackUrl = () => {
        if (screen !== REQUEST_LIBRE_FLOW) {
            return screen === PHONE_SCREEN ? AUTH_REQUEST_MAIN_LINK : '#';
        }
    };

    const sendAmplitudeEvent = () => {
        if (screen === PHONE_SCREEN) {
            amplitude.getInstance().logEvent(REQUESTEE_MOBILE_NUMBER_ADDED, { [PAYMENT_COUNTRY]: fromCountry?.nameEn });
        } else if (screen === CALCULATOR_SCREEN) {
            amplitude.getInstance().logEvent(REQUESTS_CALCULATOR_ADJUSTED);
        }
    };

    if (props.loading) {
        return null;
    }

    if (profileNotFull || profileLegacyFields) {
        return (
            <CommonPage backUrl={AUTH_REQUEST_MAIN_LINK} leftCloseIcon hasBottomPanel showChat={false}>
                <ProfileFieldsEdit popupRef={popupRef} />
            </CommonPage>
        );
    } else if (showStatus) {
        return (
            <BaseLayout showChat={false}>
                <InfoPage
                    animatedIconSuccess
                    headerText={statusTitle}
                    primaryButtonCaption={statusButtonDone}
                    primaryButtonType="link"
                    primaryButtonOnClick={(e) => {
                        e?.preventDefault();
                        props.toggleSpinner(true);
                        window.location.replace(AUTH_REQUEST_MAIN_LINK);
                    }}
                />
            </BaseLayout>
        );
    } else {
        return (
            <>
                <CommonPage backUrl={getBackUrl()} onClickIcon={onBackIconClick} leftCloseIcon={screen === PHONE_SCREEN} showChat={false}>
                    <Form formName={REQUEST_FORM_NAME} action={AUTH_REQUEST_PRECHECK} getCanonicalPath={true} onSubmitHandler={submitHandler}>
                        {screen === PHONE_SCREEN && (
                            <FromFields
                                formName={REQUEST_FORM_NAME}
                                disabledSubmitButton={setDisabledSubmitButton}
                                submitRef={submitRef}
                                switchToNextScreen={switchToNextScreen}
                            />
                        )}
                        {screen === CALCULATOR_SCREEN && <RequestCalculatorScreen formName={REQUEST_FORM_NAME} submitRef={submitRef} />}
                        {screen === REQUEST_DELIVERY_METHODS && (
                            <PaymentMethodScreen
                                formName={REQUEST_FORM_NAME}
                                sourcesCards={sourcesToBankCard}
                                sourcesAccounts={sourcesToBankAccount}
                                sourceCode={fields[SOURCE_CODE_FROM]?.value}
                                addCard={addNewCard}
                                addAccount={addNewAccount}
                                chooseLibre={chooseLibreDelivery}
                                submitRef={submitRef}
                                isCreate
                                setIsNewAccount={setIsNewAccount}
                            />
                        )}
                        {screen === REQUEST_ADDITIONAL_FIELDS && <ToFields formName={REQUEST_FORM_NAME} />}
                        {screen === REQUEST_NEW_CARD && (
                            <NewCardScreen formName={REQUEST_FORM_NAME} isCreate={true} fromCountry={fromCountry} toCountry={toCountry} />
                        )}
                        {screen === REQUEST_NEW_ACCOUNT && <NewAccountScreen formName={REQUEST_FORM_NAME} />}
                        {screen === REQUEST_LIBRE_FLOW && <RequestLibreFlow setScreen={setScreen} chooseLibreDelivery={chooseLibreDelivery} />}
                        <FixedBottomPanel isRightAligned={screen !== CALCULATOR_SCREEN}>
                            <Button
                                type="submit"
                                ref={submitRef}
                                raised={true}
                                variant={isHiddenButton() ? 'hidden' : null}
                                caption={requestContinueButtonTitle}
                                disabled={disabledSubmitButton}
                                onClick={(e) => {
                                    screenValidation(e);
                                    sendAmplitudeEvent();
                                }}
                            />
                        </FixedBottomPanel>
                    </Form>
                </CommonPage>
                <CreateRequestPopups submitRef={submitRef} popupRef={popupRef} setScreen={setScreen} popup={popup} setPopup={setPopup} />
            </>
        );
    }
};

export default connect(null, {
    toggleModalWithTimer,
    updateFormProperties,
    updateFields,
    setNewFields,
    toggleSpinner,
    setRequestDeliveryMethod,
})(BasePage(RequestPage));
